<template>
	<div
		class="form-item"
		:class="{
			'form-item--has-error': hasError,
			'form-item--half': half,
			'form-item--quarter': quarter,
			'form-item--has-empty-input': isEmpty,
			'form-item--below': errorBelow,
		}"
	>
		<label v-if="label" class="form-item__label" @click="focus">
			<slot name="label" v-bind="{ label }">
				{{ label }}
			</slot>
		</label>

		<div class="form-item__input-wrapper">
			<slot />
		</div>

		<error-message :visible="hasError" class="form-item__error">
			{{ error }}
		</error-message>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import addValidation from '@/mixins/validation/addValidation';

import ErrorMessage from './item/ErrorMessage.vue';

type InputableElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export default defineComponent({
	components: {
		ErrorMessage,
	},
	mixins: [addValidation],

	props: {
		label: {
			type: String,
			default: null,
		},
		half: {
			type: Boolean,
			default: false,
		},
		quarter: {
			type: Boolean,
			default: false,
		},
		errorBelow: {
			type: Boolean,
		},
	},

	data() {
		return {
			isEmpty: true,
		};
	},

	computed: {
		error(): string {
			return this.errorBag[0] ?? '';
		},
	},

	mounted() {
		const input = this.findInputable();

		if (input) {
			this.checkIsEmpty(input);
			input.addEventListener('input', this.onInput);
		}
	},

	beforeUnmount() {
		const input = this.findInputable();

		if (input) {
			input.removeEventListener('input', this.onInput);
		}
	},

	methods: {
		findInputable(): InputableElement | undefined {
			return this.$el.querySelector('input,select,textarea') as InputableElement | undefined;
		},

		focus() {
			const input = this.findInputable();

			if (input) {
				input.focus();
				this.checkIsEmpty(input);
			}
		},

		blur() {
			const input = this.findInputable();

			if (input) {
				input.focus();
			}
		},

		onInput({ target }: Event) {
			this.checkIsEmpty(target as InputableElement);
		},

		checkIsEmpty({ value }: InputableElement) {
			this.isEmpty = !value;
		},
	},
});
</script>

<style lang="scss">
.form-item {
	display: flex;
	flex-direction: column;
	margin-bottom: 3rem;
	position: relative;
	transition: all 300ms $easeOutQuad;
	transition-property: margin-bottom, opacity;

	&--below {
		padding-bottom: 2rem;
	}

	&:last-child {
		margin-bottom: 0;
	}

	&__label {
		@include small-text;
		display: flex;
		color: rgba($color-dark, 0.8);
	}

	&__input-wrapper {
		width: 100%;
		margin-top: auto;

		input:not([type='checkbox']),
		textarea,
		.v-select {
			width: 100%;
		}

		input.vs__search {
			width: 0;
		}

		textarea {
			min-height: 17.4rem;
		}

		input {
			@include max-lg-and-portrait {
				font-size: 2.4rem;
			}
		}

		.base-select {
			margin-top: 1.2rem;
		}
	}

	&__error {
		margin-top: 1.6rem;
		position: absolute;
		top: 100%;
		right: 0;
		left: 0;
		font-size: 1.5rem;
		text-align: right;
	}

	&--has-error {
		margin-bottom: 3.5rem;

		&:not(&--has-empty-input) {
			input,
			textarea {
				color: $color-error;
				border-color: $color-error;
			}
		}
	}
}
</style>
